package com.arena_web.data.api

import com.arena_web.data.dto.*
import com.arena_web.data.http.ApiResponse
import com.arena_web.data.http.apiDelete
import com.arena_web.data.http.apiDeleteNoBody
import com.arena_web.data.http.apiGet
import com.arena_web.data.http.apiPost
import com.arena_web.data.http.apiPostMultipart
import com.arena_web.data.http.apiPostMultipartFields
import com.arena_web.data.http.apiPut
import io.ktor.client.*
import io.ktor.http.HttpStatusCode

class CardApi(private val client: HttpClient) {

    // ─── Owned templates (ownership rows) ──────────────────────────────────
    suspend fun getMyCards(): ApiResponse<List<UserCardDto>> {
        return client.apiGet("v1/users/me/cards")
    }

    // Public read-only fetch of another user's card collection (owned +
    // available shop entries). Used by the public profile shop view.
    suspend fun getUserCards(userId: String): ApiResponse<List<UserCardDto>> {
        return client.apiGet("v1/users/$userId/cards")
    }

    suspend fun purchaseCard(request: PurchaseCardRequest): ApiResponse<List<UserCardDto>> {
        return client.apiPost("v1/users/me/cards/purchase", request)
    }

    // ─── Avatar-generation flow (per modal-open session) ───────────────────
    // `sessionId` is a UUID generated by the client when the avatar modal
    // opens. The backend scopes every attempt query by (cardId, sessionId)
    // so independent "create new card" flows that share the same underlying
    // template never see each other's attempts.
    suspend fun generateAvatar(
        cardId: String,
        sessionId: String,
        fileBytes: ByteArray,
        fileName: String,
        contentType: String,
        prompt: String? = null
    ): ApiResponse<CardAvatarAttemptDto> {
        val fields = buildMap {
            put("sessionId", sessionId)
            if (!prompt.isNullOrBlank()) put("prompt", prompt)
        }
        return client.apiPostMultipart(
            "v1/users/me/cards/$cardId/avatar/generate",
            fileBytes, fileName, contentType, fields
        )
    }

    suspend fun refineAvatar(
        cardId: String,
        sessionId: String,
        attemptId: String,
        prompt: String
    ): ApiResponse<CardAvatarAttemptDto> {
        return client.apiPostMultipartFields(
            "v1/users/me/cards/$cardId/avatar/generate",
            mapOf(
                "sessionId" to sessionId,
                "attemptId" to attemptId,
                "prompt" to prompt,
            )
        )
    }

    /** Edit mode: regenerate from an existing gallery card's photo with just a
     *  prompt (no upload needed). */
    suspend fun generateFromCard(
        cardId: String,
        sessionId: String,
        profileCardId: String,
        prompt: String,
    ): ApiResponse<CardAvatarAttemptDto> {
        return client.apiPostMultipartFields(
            "v1/users/me/cards/$cardId/avatar/generate",
            mapOf(
                "sessionId" to sessionId,
                "profileCardId" to profileCardId,
                "prompt" to prompt,
            )
        )
    }

    suspend fun getAvatarAttempts(
        cardId: String,
        sessionId: String,
    ): ApiResponse<List<CardAvatarAttemptDto>> {
        return client.apiGet(
            "v1/users/me/cards/$cardId/avatar/attempts",
            mapOf("sessionId" to sessionId),
        )
    }

    /** Confirming an attempt now spawns a new ProfileCard (gallery instance). */
    suspend fun confirmAvatar(
        cardId: String,
        request: ConfirmCardAvatarRequest
    ): ApiResponse<ProfileCardDto> {
        return client.apiPost("v1/users/me/cards/$cardId/avatar/confirm", request)
    }

    suspend fun discardAvatarAttempts(
        cardId: String,
        sessionId: String,
    ): HttpStatusCode {
        return client.apiDeleteNoBody(
            "v1/users/me/cards/$cardId/avatar/attempts?sessionId=$sessionId"
        )
    }

    // ─── Profile-card gallery (template × avatar instances) ────────────────
    suspend fun getMyProfileCards(): ApiResponse<List<ProfileCardDto>> {
        return client.apiGet("v1/users/me/profile-cards")
    }

    suspend fun getUserProfileCards(userId: String): ApiResponse<List<ProfileCardDto>> {
        return client.apiGet("v1/users/$userId/profile-cards")
    }

    suspend fun reorderProfileCards(
        request: ReorderProfileCardsRequest
    ): ApiResponse<List<ProfileCardDto>> {
        return client.apiPut("v1/users/me/profile-cards/reorder", request)
    }

    suspend fun deleteProfileCard(profileCardId: String): ApiResponse<List<ProfileCardDto>> {
        return client.apiDelete("v1/users/me/profile-cards/$profileCardId")
    }

    /** Clears the card's generated photo but keeps the card (frame + order). */
    suspend fun clearProfileCardAvatar(profileCardId: String): ApiResponse<List<ProfileCardDto>> {
        return client.apiDelete("v1/users/me/profile-cards/$profileCardId/avatar")
    }
}
